import { useEffect, type RefObject } from "react";

/**
 * A non-modal floating panel (a picker, the date picker, a popover) follows its trigger, so when the
 * list or page under it scrolls, the panel slides with the row, crosses the header and flips sides as
 * the collision logic chases the trigger. The panel closes instead when anything outside it scrolls,
 * as a native select does; a scroll inside the panel, such as its own option list, leaves it open, and
 * so does a scroll inside a panel opened from it, which portals into it (`portalContainer.tsx`).
 *
 * Only a scroll after the panel opened closes it (2026-09-30, from the meeting of 2026-09-29, Mejd:
 * "clicking start date does nothing"). A browser reports a scroll at the next rendering step, so the
 * scroll that brought a trigger into view, which a click or a keyboard move to a control below the fold
 * makes just before the open, arrived after the panel had opened and closed it at once: the date
 * pickers and every property picker below the fold of the side peek and the task dialog opened and
 * vanished. That step dispatches the pending scroll events before it runs the animation frame
 * callbacks, so the panel listens from the first frame after the open and never hears a scroll that
 * preceded it.
 *
 * Only a scroll that moved something closes it (2026-10-02, the owner's report that the "⋯" of an
 * offered meeting's popover on the meetings calendar closed everything). Firefox fires a scroll event
 * on a scrolled element whose box it rebuilds, at the offset it already had: a scroll lock that sets
 * `position: relative` on the body, as Radix's modal menus, selects and dialogs do, rebuilt the
 * calendar's scrolled hours, and the popover over them closed on an event that moved nothing. One
 * listener, added when this module loads so it has seen every scroll since, keeps the last offset of
 * each element and of the document; an element it has not seen scroll is at its start, where every
 * element starts and stays until a scroll, which it would have seen, moves it.
 */
interface Offset {
  readonly top: number;
  readonly left: number;
}

const offsets = new WeakMap<object, Offset>();
const listeners = new Set<(target: EventTarget) => void>();

function offsetOf(target: EventTarget): Offset | null {
  const element = target instanceof Document ? (target.scrollingElement ?? target.documentElement) : target instanceof Element ? target : null;
  return element === null ? null : { top: element.scrollTop, left: element.scrollLeft };
}

function onScroll(event: Event): void {
  const target = event.target;
  if (target === null) return;
  const now = offsetOf(target);
  if (now !== null) {
    const before = offsets.get(target) ?? { top: 0, left: 0 };
    offsets.set(target, now);
    if (now.top === before.top && now.left === before.left) return;
  }
  for (const listener of listeners) listener(target);
}

// Capture, because a scroll event does not bubble and the scrolling element may be any ancestor of the trigger.
if (typeof window !== "undefined") window.addEventListener("scroll", onScroll, { capture: true, passive: true });

export function useCloseOnOutsideScroll(open: boolean, panel: RefObject<HTMLElement | null>, close: () => void): void {
  useEffect(() => {
    if (!open) return;
    const listener = (target: EventTarget) => {
      if (target instanceof Node && panel.current?.contains(target) === true) return;
      close();
    };
    const frame = requestAnimationFrame(() => {
      listeners.add(listener);
    });
    return () => {
      cancelAnimationFrame(frame);
      listeners.delete(listener);
    };
  }, [open, panel, close]);
}
